<template>
  <div>
    <page-header v-if="developMode" />
    <div
      class="flex-container"
      v-if="!isMobile"
    >
      <div class="left-spaces">
        <component :is="currentLeftSpacer"></component>
      </div>
      <div class="na-wrapper">
        <progress-bar />
        <scenario />
        <step-guide v-show="!explainerGuideVisibility && !blocked" />
        <step-navigation
          v-show="navigator"
          :last-position="lastPosition"
        />
        <explainer-guide v-if="webDemoType === WEB_DEMO_TYPE_NA && explainerGuideVisibility" />
        <sta-explainer-guide v-if="webDemoType === WEB_DEMO_TYPE_STA && explainerGuideVisibility" />
      </div>
      <div class="right-spaces">
        <component :is="currentRightSpacer"></component>
      </div>
    </div>
    <page-footer v-if="developMode" />
  </div>
</template>

<script>
import PageHeader from './components/elements/PageHeader';
import PageFooter from './components/elements/PageFooter';
import Scenario from './components/scenarios/Scenario';
import StepGuide from './components/guide/StepGuide';
import ExplainerGuide from './components/guide/ExplainerGuide';
import StaExplainerGuide from './components/guide/StaExplainerGuide';
import ProgressBar from './components/utils/ProgressBar';
import StepNavigation from './components/utils/StepNavigation';
import SpacerReportList from './components/spacers/SpacerReportList';
import SpacerSearchLayout from './components/spacers/SpacerSearchLayout';
import SpacerSearchDetails from './components/spacers/SpacerSearchDetails';
import SpacerSearchDrawer from './components/spacers/SpacerSearchDrawer';
import SpacerPreviewReportTwoRows from './components/spacers/SpacerPreviewReportTwoRows';
import SpacerPreviewReportThreeRows from './components/spacers/SpacerPreviewReportThreeRows';
import SpacerPreviewReportFourRows from './components/spacers/SpacerPreviewReportFourRows';
import SpacerPreviewReportFiveRows from './components/spacers/SpacerPreviewReportFiveRows';
import SpacerPreviewReportSixRows from './components/spacers/SpacerPreviewReportSixRows';
import SpacerPreviewReportSevenRows from './components/spacers/SpacerPreviewReportSevenRows';
import SpacerPreviewReportToolsOnly from './components/spacers/SpacerPreviewReportToolsOnly';
import SpacerRiskAssessmentOverview from './components/spacers/SpacerRiskAssessmentOverview';
import SpacerNdcAppTabsLayoutLeft from './components/spacers/SpacerNdcAppTabsLayoutLeft';
import SpacerNdcAppTabsLayoutRight from './components/spacers/SpacerNdcAppTabsLayoutRight';
import SpacerNdcAppTaxonomiesLayoutLeft from './components/spacers/SpacerNdcAppTaxonomiesLayoutLeft';
import SpacerStaApplication from './components/spacers/SpacerStaApplication';
import SpacerStaApplicationBreadcrumbs from './components/spacers/SpacerStaApplicationBreadcrumbs';
import SpacerStaApplicationBreadcrumbsDescription
  from './components/spacers/SpacerStaApplicationBreadcrumbsDescription';

import { mapState } from 'vuex';
import { mapActions } from 'vuex';

import {
  EVENT_GA_SEND,
  EVENT_LOG,
  EVENT_SET_SCENARIO,
  EVENT_DEMO_LINK,
  WEB_DEMO_TYPE,
  WEB_DEMO_TYPE_NA,
  WEB_DEMO_TYPE_STA,
  LANGUAGE_DEFAULT,
  LANGUAGE_ENGLISH,
  LANGUAGE_FRENCH,
  EVENT_EXPLAINER_DONE,
  EVENT_EXPLAINER_SHOW,
} from './constants';

export default {
  name: 'app',
  components: {
    PageHeader,
    PageFooter,
    Scenario,
    StepGuide,
    ExplainerGuide,
    StaExplainerGuide,
    ProgressBar,
    StepNavigation,
    SpacerReportList,
    SpacerSearchLayout,
    SpacerSearchDetails,
    SpacerSearchDrawer,
    SpacerPreviewReportTwoRows,
    SpacerPreviewReportThreeRows,
    SpacerPreviewReportFourRows,
    SpacerPreviewReportFiveRows,
    SpacerPreviewReportSixRows,
    SpacerPreviewReportSevenRows,
    SpacerPreviewReportToolsOnly,
    SpacerRiskAssessmentOverview,
    SpacerNdcAppTabsLayoutLeft,
    SpacerNdcAppTabsLayoutRight,
    SpacerNdcAppTaxonomiesLayoutLeft,
    SpacerStaApplication,
    SpacerStaApplicationBreadcrumbs,
    SpacerStaApplicationBreadcrumbsDescription,
  },
  data() {
    return {
      WEB_DEMO_TYPE_NA,
      WEB_DEMO_TYPE_STA,
      isDemoActive: true,
      isExplainerAllowed: window.explainerGuide ? window.explainerGuide : false,
      isExplainerVisible: false,
      lastPosition: {},
      userEmail: window.userEmail,
      language: window.language ? window.language : LANGUAGE_DEFAULT,
      isMobile: window.isMobile ? window.isMobile : false,
      scenario: null,
      webDemoDefaultType: window.webDemoType || WEB_DEMO_TYPE,
    };
  },
  computed: {
    ...mapState('app', ['developMode', 'logs', 'currentLeftSpacer', 'currentRightSpacer']),
    ...mapState('guide', ['scenariosMap', 'scenariosNameMap', 'blocked', 'webDemoType', 'currentScenarioId']),
    demoUrl() {
      let url = '';
      switch (this.language) {
        case LANGUAGE_ENGLISH:
          url = 'https://www.netwrix.com/one-to-one.html?from=demo';
          break;
        case LANGUAGE_FRENCH:
          url = 'https://www.netwrix.fr/one-to-one.html';
          break;
        default:
          url = 'https://www.netwrix.com/one-to-one.html?from=demo';
      }
      return url;
    },
    navigator() {
      return this.developMode || this.getCookie('ibdnav');
    },
    explainerGuideVisibility() {
      return this.language === LANGUAGE_ENGLISH &&
        this.currentScenarioId === 0 &&
        this.isExplainerAllowed === true &&
        this.isExplainerVisible === true &&
        this.blocked === false;
    },
  },
  methods: {
    ...mapActions('guide', ['setLanguage', 'setWebDemoType', 'lockApp', 'unlockApp']),
    getCookie(name) {
      const matches = document.cookie.match(new RegExp(
        '(?:^|; )' + name.replace(/([.$?*|{}()[]\\\/+^])/g, '\\$1') + '=([^;]*)',
      ));
      return matches ? decodeURIComponent(matches[1]) : undefined;
    },
    setCookie(name, value, options) {
      options = options || {};

      let expires = options.expires;

      if (typeof expires == 'number' && expires) {
        let d = new Date();
        d.setTime(d.getTime() + expires * 1000);
        expires = options.expires = d;
      }
      if (expires && expires.toUTCString) {
        options.expires = expires.toUTCString();
      }

      value = encodeURIComponent(value);

      let updatedCookie = name + '=' + value;

      for (const propName in options) {
        updatedCookie += '; ' + propName;
        let propValue = options[propName];
        if (propValue !== true) {
          updatedCookie += '=' + propValue;
        }
      }

      document.cookie = updatedCookie;
    },
    openDemoPage() {
      window.open(this.demoUrl);
    },
    afterCreateLead() {
      const scenarioObj = this.scenariosMap[this.webDemoType].get(this.scenario);
      if (scenarioObj.id === 0) {
        this.clearUrlParam();
      } else {
        this.$root.$emit(EVENT_GA_SEND, { scenario: scenarioObj.name, stage: 'Started' });
        this.changeUrlParam('scenario', this.scenariosNameMap[this.webDemoType].get(scenarioObj.id));
      }
    },
    updateUrlParams: function (urlParams, key, val) {
      const newKeyVal = key + '=' + val;

      const params = (urlParams.length) ? urlParams.slice(1).split('&') : [];

      if (params.indexOf(newKeyVal) < 0) {
        params.push(newKeyVal);
      }

      return '?' + params.join('&');
    },
    changeUrlParam: function (param, value) {
      const params = this.updateUrlParams(location.search, param, value);
      const newUrl = location.pathname + params;

      history.replaceState(null, null, newUrl);
    },
    clearUrlParam() {
      history.replaceState(null, null, location.pathname);
    },
    loadCustomScenario(customScenario) {
      const scenarioObj = this.scenariosMap[this.webDemoType].get(customScenario);

      if (scenarioObj.id) {
        this.$root.$emit(EVENT_SET_SCENARIO, { value: scenarioObj.id });
      } else {
        this.$root.$emit(EVENT_SET_SCENARIO, { value: 0 });
        this.$root.$emit(EVENT_LOG, { message: 'incorrect scenario', type: 2 });
      }
    },
    main() {
      if (this.developMode) {
        this.unlockApplication({source: 'main'});
        this.loadCustomScenario(this.scenario);
      } else {
        const scenarioObj = this.scenariosMap[this.webDemoType].get(this.scenario);

        if (scenarioObj.creationLeadRequired) {
          this.requestLeadCreation(scenarioObj);
        }

        this.loadCustomScenario(this.scenario);
      }
    },
    requestLeadCreation(scenarioObj) {
      if (this.scenario) {
        this.lockApplication({source: 'requestLeadCreation'});

        const event = new CustomEvent('lead:request', {
          detail: {
            product: scenarioObj.product,
            name: scenarioObj.name,
            topicAction: scenarioObj.topicAction,
            contentStage: scenarioObj.contentStage,
          },
        });
        document.dispatchEvent(event);
      }
    },
    lockApplication() {
      this.lockApp();
    },
    unlockApplication() {
      this.unlockApp();
    },
    log(log) {
      if (!this.logs) return false;

      log.type = log.type || 0;
      log.message = log.message || '';

      switch (log.type) {
        case 1:
          // eslint-disable-next-line
          console.warn(log.message);
          break;
        case 2:
          // eslint-disable-next-line
          console.error(log.message);
          break;
        default:
          // eslint-disable-next-line
          console.log(log.message);
      }
    },
    gaSend(params) {
      params = params.value || params || {};
      if (typeof ga === 'function') {
        // eslint-disable-next-line
        ga('send', 'event', 'InBrowser Scenario', params.scenario, params.stage);
      }
    },
    showExplainer() {
      this.isExplainerVisible = true;
    },
    hideExplainer() {
      this.isExplainerAllowed = false;
      this.isExplainerVisible = false;
      document.dispatchEvent(new CustomEvent('guide:set-cookie'));
    },
  },
  created() {
    this.lockApplication({source: 'created'});
    this.setLanguage({ value: this.language });
    this.setWebDemoType({ value: this.webDemoDefaultType });

    if (window.customScenario) {
      this.scenario = window.customScenario;
    } else {
      switch (this.webDemoType) {
        case 1:
          this.scenario = 'ndc_welcome';
          break;
        case 2:
          this.scenario = 'sta_welcome';
          break;
        default:
          this.scenario = 'welcome';
      }
    }

    this.lastPosition = {
      left: this.getCookie('ibdleft') || 0,
      top: this.getCookie('ibdtop') || 0,
    };
  },
  mounted() {
    this.$root.$on(EVENT_DEMO_LINK, this.openDemoPage);
    this.$root.$on(EVENT_GA_SEND, this.gaSend);
    this.$root.$on(EVENT_LOG, this.log);
    this.$root.$on(EVENT_EXPLAINER_DONE, this.hideExplainer);
    this.$root.$on(EVENT_EXPLAINER_SHOW, this.showExplainer);
    this.main();

    document.addEventListener('app:lock', this.lockApplication);
    document.addEventListener('app:unlock', this.unlockApplication);
    document.addEventListener('app:gaevent', this.afterCreateLead);
  },
  beforeDestroy() {
    this.$root.$off(EVENT_DEMO_LINK);
    this.$root.$off(EVENT_GA_SEND);
    this.$root.$off(EVENT_LOG);
    this.$root.$off(EVENT_EXPLAINER_DONE);
    this.$root.$off(EVENT_EXPLAINER_SHOW);

    document.removeEventListener('app:lock', this.lockApplication);
    document.removeEventListener('app:unlock', this.unlockApplication);
    document.removeEventListener('app:gaevent', this.afterCreateLead);
  },
};
</script>
<style lang="less">
  @import "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.4.0/css/font-awesome.min.css";

  .flex-container {
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
  }

  .left-spaces, .right-spaces {
    padding: 40px 0;
    display: flex;
    align-items: stretch;
  }

  .left-spaces {
    flex: 1 0 0;
    min-width: 22px;
  }

  .right-spaces {
    flex: 1 0 0;
  }

  .na-wrapper {
    position: relative;
    height: auto;
    padding: 40px 0;
    min-height: 768px;
    flex: 0 0 1445px;
  }

  .filter-body {
    padding-top: 25px;
    padding-bottom: 10px;
  }
</style>
